import React, { useState, useMemo } from "react";
import BaseChart from "./BaseChart";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "./select";
import { exportToCSV } from "../../lib/post_analysis_utils/exportToCSV";

export default function Panel({
  title,
  chartData,
  yAxis,
  onYChange,
  tableData,
  TableComponent,
  yAxisOptions,
  sortOptions,
  defaultSort = "Highest First",
  chartType = "bar",
  chartColor = "#2c7392",
  xKey = "name",
  yKey = "value",
  yAxisFormatter,
  xAxisFormatter,
  formatValue,
}) {
  const [view, setView] = useState("chart");
  const [sortBy, setSortBy] = useState(defaultSort);
  const [displayCount, setDisplayCount] = useState(10);

  const processedChartData = useMemo(() => {
    let sorted = [...chartData];
    if (sortOptions && sortOptions[sortBy]) {
      sorted = sorted.sort(sortOptions[sortBy]);
    }
    if (title !== "Regional View") {
      sorted = sorted.slice(0, displayCount);
    }
    return sorted;
  }, [chartData, sortBy, displayCount, sortOptions, title]);

  const isRegionalView = title === "Regional View";
  const isBrandView = title ==="Brand View"

  // Set axis labels based on panel title
  let xLabel = "";
  if (title.includes("POC")) xLabel = "POC";
  else if (title.includes("Region")) xLabel = "Region";
  else if (title.includes("Brand")) xLabel = "Brand";
  else if (title.includes("Campaign")) xLabel = "Campaign";
  else xLabel = xKey.charAt(0).toUpperCase() + xKey.slice(1);
  const yLabel = yAxis;

  const handleExport = () => {
    if (tableData && tableData.length > 0) {
      exportToCSV(tableData, `${title.replace(/\s+/g, '_').toLowerCase()}.csv`);
    }
  };

  return (
    <div className="bg-background text-foreground border border-border rounded-lg shadow p-4 h-full flex flex-col">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-semibold">{title}</h2>
        <button onClick={() => setView(v => v === "table" ? "chart" : "table")} className="text-sm text-foreground hover:underline">
          Toggle {view === "table" ? "Chart" : "Table"}
        </button>
      </div>

      {view === "table" ? (
        <>
          <div className="flex justify-end mb-2">
            {tableData && tableData.length > 0 && (
              <button
                className="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm"
                onClick={handleExport}
              >
                Export CSV
              </button>
            )}
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto flex-grow">
            {TableComponent && <TableComponent raw_data={tableData} />}
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-2 min-w-[300px]">
              <label className="text-sm font-medium whitespace-nowrap w-16 text-foreground">Y-Axis:</label>
              <Select className="w-[200px]" value={yAxis} onValueChange={onYChange}>
                <SelectTrigger className="w-[200px] bg-background border border-border rounded-md">
                  <SelectValue placeholder="Impressions" />
                </SelectTrigger>
                <SelectContent className="w-[200px] bg-background border border-border">
                  <SelectGroup>
                    {yAxisOptions && yAxisOptions.map((opt) => (
                      <SelectItem key={opt} value={opt} className="text-foreground">{opt}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            {sortOptions && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium whitespace-nowrap w-16 text-foreground">Sort:</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[150px] bg-background border border-border rounded-md">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent className="bg-background border border-border">
                    <SelectGroup>
                      {Object.keys(sortOptions).map(opt => (
                        <SelectItem key={opt} value={opt} className="text-foreground">{opt}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
            {!isRegionalView && !isBrandView &&(
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium whitespace-nowrap w-16 text-foreground">Show Top:</label>
                <Select value={displayCount.toString()} onValueChange={v => setDisplayCount(Number(v))}>
                  <SelectTrigger className="w-[100px] bg-background border border-border rounded-md">
                    <SelectValue placeholder="Show..." />
                  </SelectTrigger>
                  <SelectContent className="bg-background border border-border">
                    <SelectGroup>
                      {[10, 20, 30, 50, 100].map(n => (
                        <SelectItem key={n} value={n.toString()} className="text-foreground">{n}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex-grow h-[400px] bg-background rounded-l">
            <BaseChart
              data={processedChartData}
              xKey={xKey}
              yKey={yKey}
              type={chartType}
              color={chartColor}
              height={400}
              yAxisFormatter={yAxisFormatter}
              xAxisFormatter={xAxisFormatter}
              xLabel={xLabel}
              yLabel={yLabel}
              tooltipFormatter={formatValue}
            />
          </div>
          {!isRegionalView && chartData.length > displayCount && (
            <p className="text-sm text-muted-foreground mt-2 text-center">
              Showing top {displayCount} of {chartData.length} items
            </p>
          )}
        </>
      )}
    </div>
  );
} 